import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import apiClient from "../api/client";
import "./CollateralPage.css";
import { useCurrentUser } from "../hooks/useCurrentUser";
import { can } from "../utils/permissions";
import NumericInput from "./ui/NumericInput";
import CompanySelect from "./ui/CompanySelect";
import BasketsTab from "./BasketsTab";
import { groupHoldingsByAsset } from "../utils/assetGrouping";


// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatILS(value, compact = false) {
  if (value == null || value === "" || isNaN(value)) return "—";
  if (compact && Math.abs(value) >= 1_000_000) {
    return `₪${(value / 1_000_000).toFixed(1)}M`;
  }
  return new Intl.NumberFormat("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPct(val, decimals = 1) {
  if (val == null || isNaN(val)) return "—";
  return `${Number(val).toFixed(decimals)}%`;
}

function formatNum(val) {
  if (val == null || isNaN(val)) return "—";
  return Number(val).toLocaleString("he-IL");
}

function formatDate(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("he-IL");
}

function isExpiringSoon(date, withinDays = 90) {
  if (!date) return false;
  const days = (new Date(date).getTime() - Date.now()) / (24 * 60 * 60 * 1000);
  return days >= 0 && days <= withinDays;
}

function ltvStatusConfig(status) {
  switch (status) {
    case "BREACH":
      return { label: "הפרה", cls: "status-breach" };
    case "WARNING":
      return { label: "אזהרה", cls: "status-warning" };
    case "SAFE":
      return { label: "תקין", cls: "status-safe" };
    case "RELEASE_ELIGIBLE":
      return { label: "ניתן לשחרר", cls: "status-release" };
    case "NO_PRICE":
      return { label: "אין מחיר", cls: "status-none" };
    default:
      return { label: "ללא בטוחה", cls: "status-none" };
  }
}

// LTV gauge: clamp 0–200%
function LTVGauge({ ltv, warning, breach, size = 80 }) {
  if (ltv == null) return <div className="ltv-gauge-empty">—</div>;
  const pct = Math.min(ltv, 200);
  const angle = (pct / 200) * 180;
  const warningAngle = warning ? Math.min(warning, 200) / 200 * 180 : null;
  const breachAngle = breach ? Math.min(breach, 200) / 200 * 180 : null;
  const r = size * 0.42;
  const cx = size / 2;
  const cy = size * 0.55;

  function polarToXY(angleDeg) {
    const rad = ((angleDeg - 180) * Math.PI) / 180;
    return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
  }

  const start = polarToXY(0);
  const needleEnd = polarToXY(angle);
  const color = ltv >= (breach || 999) ? "var(--cp-breach)" : ltv >= (warning || 999) ? "var(--cp-warning)" : "var(--cp-safe)";

  return (
    <svg width={size} height={size * 0.65} viewBox={`0 0 ${size} ${size * 0.65}`} className="ltv-gauge-svg">
      {/* Track */}
      <path d={`M ${polarToXY(0).x} ${polarToXY(0).y} A ${r} ${r} 0 0 1 ${polarToXY(180).x} ${polarToXY(180).y}`}
        fill="none" stroke="var(--border)" strokeWidth={size * 0.1} strokeLinecap="round" />
      {/* Fill */}
      {angle > 0 && (
        <path d={`M ${start.x} ${start.y} A ${r} ${r} 0 ${angle > 180 ? 1 : 0} 1 ${needleEnd.x} ${needleEnd.y}`}
          fill="none" stroke={color} strokeWidth={size * 0.1} strokeLinecap="round" />
      )}
      {/* Warning marker */}
      {warningAngle != null && (() => {
        const p = polarToXY(warningAngle);
        const inner = { x: cx + (r - size * 0.13) * Math.cos(((warningAngle - 180) * Math.PI) / 180), y: cy + (r - size * 0.13) * Math.sin(((warningAngle - 180) * Math.PI) / 180) };
        return <line x1={p.x} y1={p.y} x2={inner.x} y2={inner.y} stroke="var(--cp-warning)" strokeWidth={1.5} />;
      })()}
      {/* Breach marker */}
      {breachAngle != null && (() => {
        const p = polarToXY(breachAngle);
        const inner = { x: cx + (r - size * 0.13) * Math.cos(((breachAngle - 180) * Math.PI) / 180), y: cy + (r - size * 0.13) * Math.sin(((breachAngle - 180) * Math.PI) / 180) };
        return <line x1={p.x} y1={p.y} x2={inner.x} y2={inner.y} stroke="var(--cp-breach)" strokeWidth={1.5} />;
      })()}
      {/* Center value */}
      <text x={cx} y={cy * 1.05} textAnchor="middle" fontSize={size * 0.2} fontWeight="700" fill={color}>
        {formatPct(ltv)}
      </text>
    </svg>
  );
}

// ─── Portfolio Pulse Header ────────────────────────────────────────────────────

function PulseHeader({ loans, holdings }) {
  const stats = useMemo(() => {
    const byStatus = { BREACH: 0, WARNING: 0, SAFE: 0, RELEASE_ELIGIBLE: 0, NO_COLLATERAL: 0 };
    let totalDebt = 0;
    let totalCollateral = 0;

    // Deduplicate basket members: count each named basket once for status badges
    const seenBaskets = new Set();

    loans.forEach((l) => {
      if (l.status === "repaid") return;
      // For status counts, count named baskets once
      if (l.basketName && l.basket) {
        const bid = String(l.basket._id);
        if (!seenBaskets.has(bid)) {
          seenBaskets.add(bid);
          if (l.ltvStatus) byStatus[l.ltvStatus] = (byStatus[l.ltvStatus] || 0) + 1;
        }
      } else {
        if (l.ltvStatus) byStatus[l.ltvStatus] = (byStatus[l.ltvStatus] || 0) + 1;
      }
      // Debt and collateral: sum all loans individually (basket fields already represent basket-level aggregates)
      if (l.netDebt != null) totalDebt += l.netDebt;
      // Stock collateral: derive value from LTV
      if (l.currentLTV != null && l.netDebt != null && l.currentLTV > 0) {
        totalCollateral += (l.netDebt / (l.currentLTV / 100));
      }
    });

    // Add non-stock registry collateral: each pledged holding's full value, once.
    // Holdings are enriched server-side from active pledges (isPledged/pledgedValue).
    (holdings || []).forEach((h) => {
      if (h.assetType === "STOCK") return; // STOCK already captured via LTV above
      if (h.pledgedValue) totalCollateral += h.pledgedValue;
    });

    const aggregateLTV = totalCollateral > 0 ? (totalDebt / totalCollateral) * 100 : null;
    return { ...byStatus, totalDebt, aggregateLTV };
  }, [loans, holdings]);

  return (
    <div className="cp-pulse-header">
      <div className="cp-pulse-title">
        <span className="cp-pulse-label">סטטוס בטחונות</span>
      </div>
      <div className="cp-pulse-chips">
        {stats.BREACH > 0 && (
          <div className="cp-status-chip cp-chip-breach">
            <span className="cp-chip-count">{stats.BREACH}</span>
            <span className="cp-chip-label">הפרה</span>
          </div>
        )}
        {stats.WARNING > 0 && (
          <div className="cp-status-chip cp-chip-warning">
            <span className="cp-chip-count">{stats.WARNING}</span>
            <span className="cp-chip-label">אזהרה</span>
          </div>
        )}
        {stats.SAFE > 0 && (
          <div className="cp-status-chip cp-chip-safe">
            <span className="cp-chip-count">{stats.SAFE}</span>
            <span className="cp-chip-label">תקין</span>
          </div>
        )}
        {stats.RELEASE_ELIGIBLE > 0 && (
          <div className="cp-status-chip cp-chip-release">
            <span className="cp-chip-count">{stats.RELEASE_ELIGIBLE}</span>
            <span className="cp-chip-label">ניתן לשחרר</span>
          </div>
        )}
      </div>
      <div className="cp-pulse-meta">
        <div className="cp-pulse-meta-item">
          <span className="cp-pulse-meta-label">חוב נטו כולל</span>
          <span className="cp-pulse-meta-value">{formatILS(stats.totalDebt, true)}</span>
        </div>
        {stats.aggregateLTV != null && (
          <div className="cp-pulse-meta-item">
            <span className="cp-pulse-meta-label">LTV ממוצע משוקלל</span>
            <span className={`cp-pulse-meta-value ${stats.aggregateLTV > 80 ? "text-breach" : stats.aggregateLTV > 60 ? "text-warning" : "text-safe"}`}>
              {formatPct(stats.aggregateLTV)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab 1: LTV Monitor ───────────────────────────────────────────────────────

function LTVCard({ loan }) {
  const [expanded, setExpanded] = useState(false);
  const [breachNote, setBreachNote] = useState("");
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteSuccess, setNoteSuccess] = useState(false);
  const cfg = ltvStatusConfig(loan.ltvStatus);
  // Thresholds and covenant live on the loan's basket (a singleton for the loans
  // this card renders); enrichLoansWithBasketLTV attaches them as loan.basket.
  const basket = loan.basket || {};

  const handleBreachNote = async () => {
    if (!breachNote.trim()) return;
    setNoteSubmitting(true);
    try {
      await apiClient.post(`/loans/${loan._id}/breach-note`, { note: breachNote.trim() });
      setBreachNote("");
      setNoteSuccess(true);
      setTimeout(() => setNoteSuccess(false), 3000);
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setNoteSubmitting(false);
    }
  };

  const priceFormula = { SPOT: "שער ידוע", "3DAY_AVG": "ממוצע 3י׳", "5DAY_AVG": "ממוצע 5י׳", "90DAY_AVG": "ממוצע 90י׳" };

  return (
    <div className={`cp-ltv-card ${loan.ltvStatus ? `cp-ltv-card-${loan.ltvStatus}` : "cp-ltv-card-none"}`}>
      <div className="cp-ltv-card-header" onClick={() => setExpanded((v) => !v)}>
        <div className="cp-ltv-card-header-row">
          <div className="cp-ltv-card-identity">
            <span className={`cp-status-badge ${cfg.cls}`}>{cfg.label}</span>
            <div className="cp-ltv-card-lender">{loan.lender}</div>
            <div className="cp-ltv-card-company">{loan.borrowerCompany}</div>
          </div>
          <div className="cp-ltv-card-gauge-area">
            {loan.currentLTV != null ? (
              <>
                <LTVGauge
                  ltv={loan.currentLTV}
                  warning={basket.ltvWarningThreshold}
                  breach={basket.ltvBreachThreshold}
                  size={100}
                />
                <span className="cp-ltv-gauge-label">LTV נוכחי</span>
              </>
            ) : (
              <div className="cp-ltv-no-stock">ללא מניות</div>
            )}
          </div>
          <div className="cp-ltv-card-summary">
            <div className="cp-ltv-summary-row">
              <span className="cp-ltv-summary-label">חוב נטו</span>
              <span className="cp-ltv-summary-value">{formatILS(loan.netDebt, true)}</span>
            </div>
            {loan.vtl != null && (
              <div className="cp-ltv-summary-row">
                <span className="cp-ltv-summary-label">VTL</span>
                <span className="cp-ltv-summary-value">{formatPct(loan.vtl)}</span>
              </div>
            )}
            {loan.collateralValue != null && (
              <div className="cp-ltv-summary-row">
                <span className="cp-ltv-summary-label">שווי בטוחה</span>
                <span className="cp-ltv-summary-value">{formatILS(loan.collateralValue, true)}</span>
              </div>
            )}
            {loan.pctToBreachLTV != null && (
              <div className="cp-ltv-summary-row">
                <span className="cp-ltv-summary-label">מרחק מהפרה</span>
                <span className={`cp-ltv-summary-value ${loan.pctToBreachLTV < 10 ? "text-breach" : loan.pctToBreachLTV < 20 ? "text-warning" : "text-safe"}`}>
                  {formatPct(loan.pctToBreachLTV)}
                </span>
              </div>
            )}
          </div>
          <button className="cp-expand-btn" aria-label={expanded ? "כווץ" : "הרחב"}>
            <span className={`cp-expand-chevron${expanded ? " open" : ""}`} />
          </button>
        </div>
        {loan.pctToBreachLTV != null && loan.pctToBreachLTV < 10 && loan.pctToBreachLTV > 0 && (
          <div className="cp-price-warning-banner">
            <span className="cp-price-warning-icon">⚠</span>
            <span>התראת מחיר: שער נוכחי {formatPct(loan.pctToBreachLTV)} מרצפת הפרה</span>
          </div>
        )}
        {loan.currentLTV != null && (
          <LTVTrackBar
            ltv={loan.currentLTV}
            warning={basket.ltvWarningThreshold}
            breach={basket.ltvBreachThreshold}
          />
        )}
      </div>

      {expanded && (
        <div className="cp-ltv-card-detail">
          <div className="cp-detail-grid">
            <DetailItem label="שיטת מחיר" value={priceFormula[basket.ltvPriceFormula] || "שער ידוע"} />
            <DetailItem label="קרן נותרת" value={formatILS(loan.remainingPrincipal || loan.principal)} />
            <DetailItem label="פיקדון מזומן" value={formatILS(loan.cashDepositAmount)} />
            <DetailItem label="מחיר נוכחי (₪)" value={loan.currentStockPrice ? formatILS(loan.currentStockPrice) : "—"} />
            <DetailItem label="שווי בטוחה" value={loan.currentStockValue ? formatILS(loan.currentStockValue, true) : "—"} />
            {basket.ltvWarningThreshold && (
              <DetailItem label="סף אזהרה" value={formatPct(basket.ltvWarningThreshold)} />
            )}
            {basket.ltvBreachThreshold && (
              <DetailItem label="סף הפרה" value={formatPct(basket.ltvBreachThreshold)} />
            )}
            {loan.floorPrice != null && (
              <DetailItem label="מחיר רצפה — הפרה (₪)" value={formatILS(loan.floorPrice)} />
            )}
            {loan.cashShortfall > 0 && (
              <DetailItem label="חוסר בטחונות (₪)" value={formatILS(loan.cashShortfall)} highlight />
            )}
            {loan.sharesNeededToRemedy > 0 && (
              <DetailItem label="השלמה במניות" value={`${formatNum(loan.sharesNeededToRemedy)} מניות`} highlight />
            )}
            {loan.vtl != null && (
              <DetailItem label="VTL (שווי/חוב)" value={formatPct(loan.vtl)} />
            )}
            {loan.surplusShares != null && loan.surplusShares > 0 && (
              <DetailItem label="מניות לשחרור" value={`${formatNum(Math.floor(loan.surplusShares))} מניות`} highlight />
            )}
            {basket.canReleaseStockAtThreshold && basket.stockReleaseLTVMin && (
              <DetailItem label="חלון שחרור LTV" value={`${formatPct(basket.stockReleaseLTVMin)} – ${formatPct(basket.stockReleaseLTVMax)}`} />
            )}
          </div>
          {basket.covenantText && (
            <div className="cp-covenant-text">
              <div className="cp-covenant-label">טקסט קובנאנט</div>
              <div className="cp-covenant-body">{basket.covenantText}</div>
            </div>
          )}
          {loan.ltvStatus === "BREACH" && (
            <div className="cp-breach-note-area">
              {noteSuccess ? (
                <div className="cp-breach-note-success">✓ הפעולה נרשמה ביומן הפעילות</div>
              ) : (
                <>
                  <textarea
                    className="cp-breach-note-input"
                    value={breachNote}
                    onChange={(e) => setBreachNote(e.target.value)}
                    placeholder="תאר את הפעולה שננקטה (שיחה עם לווה, הודעת מרג׳ין, וכו׳)..."
                    rows={2}
                  />
                  <button
                    type="button"
                    className="btn btn-primary btn-sm"
                    onClick={handleBreachNote}
                    disabled={noteSubmitting || !breachNote.trim()}
                  >
                    {noteSubmitting ? "שומר..." : "רשום פעולה"}
                  </button>
                </>
              )}
            </div>
          )}
          <div className="cp-detail-actions">
            <Link to={`/loan/${loan._id}`} className="cp-detail-link">פתח הלוואה ←</Link>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailItem({ label, value, highlight }) {
  return (
    <div className={`cp-detail-item ${highlight ? "cp-detail-highlight" : ""}`}>
      <span className="cp-detail-label">{label}</span>
      <span className="cp-detail-value">{value}</span>
    </div>
  );
}

function LTVTrackBar({ ltv, warning, breach }) {
  if (ltv == null) return null;
  const scale = Math.max(breach ? breach * 1.4 : 100, ltv * 1.15, 100);
  const fillPct = Math.min((ltv / scale) * 100, 100);
  const warningPct = warning ? (warning / scale) * 100 : null;
  const breachPct = breach ? (breach / scale) * 100 : null;
  const color =
    ltv >= (breach || 9999) ? "var(--cp-breach)" :
    ltv >= (warning || 9999) ? "var(--cp-warning)" :
    "var(--cp-safe)";

  return (
    <div className="cp-ltv-track">
      <div className="cp-ltv-track-bar">
        <div className="cp-ltv-track-fill" style={{ width: `${fillPct}%`, background: color }} />
        {warningPct != null && (
          <div className="cp-ltv-track-marker cp-ltv-track-marker-warning" style={{ left: `${warningPct}%` }}>
            <span className="cp-ltv-track-tick-label" style={{ color: "var(--cp-warning)" }}>
              {formatPct(warning, 0)}
            </span>
          </div>
        )}
        {breachPct != null && (
          <div className="cp-ltv-track-marker cp-ltv-track-marker-breach" style={{ left: `${breachPct}%` }}>
            <span className="cp-ltv-track-tick-label" style={{ color: "var(--cp-breach)" }}>
              {formatPct(breach, 0)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── BasketLTVCard: one card per named basket in monitor ──────────────────────

function BasketLTVCard({ card }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = ltvStatusConfig(card.ltvStatus);
  // Thresholds and covenant come from the basket enrichLoansWithBasketLTV attaches,
  // not flat on the loan.
  const basket = card.basket || {};

  const priceFormula = { SPOT: "שער ידוע", "3DAY_AVG": "ממוצע 3י׳", "5DAY_AVG": "ממוצע 5י׳", "90DAY_AVG": "ממוצע 90י׳" };

  return (
    <div className={`cp-ltv-card ${card.ltvStatus ? `cp-ltv-card-${card.ltvStatus}` : "cp-ltv-card-none"}`}>
      <div className="cp-ltv-card-header" onClick={() => setExpanded((v) => !v)}>
        <div className="cp-ltv-card-header-row">
          <div className="cp-ltv-card-identity">
            <span className={`cp-status-badge ${cfg.cls}`}>{cfg.label}</span>
            <div className="cp-ltv-card-lender">{card.cardTitle}</div>
            <div className="cp-ltv-card-company">
              מסגרת · {card.memberLoans.length} הלוואות
            </div>
          </div>
          <div className="cp-ltv-card-gauge-area">
            {card.currentLTV != null ? (
              <>
                <LTVGauge
                  ltv={card.currentLTV}
                  warning={basket.ltvWarningThreshold}
                  breach={basket.ltvBreachThreshold}
                  size={100}
                />
                <span className="cp-ltv-gauge-label">LTV מסגרת</span>
              </>
            ) : (
              <div className="cp-ltv-no-stock">ללא מניות</div>
            )}
          </div>
          <div className="cp-ltv-card-summary">
            <div className="cp-ltv-summary-row">
              <span className="cp-ltv-summary-label">חוב נטו</span>
              <span className="cp-ltv-summary-value">{card.netDebt != null ? new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(card.netDebt) : "—"}</span>
            </div>
            {card.collateralValue != null && (
              <div className="cp-ltv-summary-row">
                <span className="cp-ltv-summary-label">שווי בטוחה</span>
                <span className="cp-ltv-summary-value">{new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(card.collateralValue)}</span>
              </div>
            )}
            {card.pctToBreachLTV != null && (
              <div className="cp-ltv-summary-row">
                <span className="cp-ltv-summary-label">מרחק מהפרה</span>
                <span className={`cp-ltv-summary-value ${card.pctToBreachLTV < 10 ? "text-breach" : card.pctToBreachLTV < 20 ? "text-warning" : "text-safe"}`}>
                  {`${Number(card.pctToBreachLTV).toFixed(1)}%`}
                </span>
              </div>
            )}
          </div>
          <button className="cp-expand-btn" aria-label={expanded ? "כווץ" : "הרחב"}>
            <span className={`cp-expand-chevron${expanded ? " open" : ""}`} />
          </button>
        </div>
        {card.currentLTV != null && (
          <LTVTrackBar
            ltv={card.currentLTV}
            warning={basket.ltvWarningThreshold}
            breach={basket.ltvBreachThreshold}
          />
        )}
      </div>

      {expanded && (
        <div className="cp-ltv-card-detail">
          {/* Member loans list */}
          <div style={{ marginBottom: 12 }}>
            <div className="cp-covenant-label">הלוואות במסגרת</div>
            {card.memberLoans.map((l) => (
              <div key={l._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: "1px solid var(--border-light)", fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{l.lender}</span>
                <span style={{ color: "var(--text-secondary)" }}>{l.borrowerCompany}</span>
                {l.remainingPrincipal != null && (
                  <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>
                    {new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(l.remainingPrincipal)}
                  </span>
                )}
                <Link to={`/loan/${l._id}`} className="cp-table-link">→</Link>
              </div>
            ))}
          </div>
          <div className="cp-detail-grid">
            {basket.ltvWarningThreshold && (
              <DetailItem label="סף אזהרה" value={`${Number(basket.ltvWarningThreshold).toFixed(1)}%`} />
            )}
            {basket.ltvBreachThreshold && (
              <DetailItem label="סף הפרה" value={`${Number(basket.ltvBreachThreshold).toFixed(1)}%`} />
            )}
            {basket.ltvPriceFormula && (
              <DetailItem label="שיטת מחיר" value={priceFormula[basket.ltvPriceFormula] || basket.ltvPriceFormula} />
            )}
            {card.guaranteeTotal > 0 && (
              <DetailItem label="ערבויות" value={new Intl.NumberFormat("he-IL", { style: "currency", currency: "ILS", maximumFractionDigits: 0 }).format(card.guaranteeTotal)} />
            )}
          </div>
          {basket.covenantText && (
            <div className="cp-covenant-text">
              <div className="cp-covenant-label">טקסט קובנאנט</div>
              <div className="cp-covenant-body">{basket.covenantText}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LTVMonitor({ loans }) {
  // Group loans by named basket; singleton/standalone loans render individually
  const monitorCards = useMemo(() => {
    const cards = [];
    const seenBaskets = new Set();
    for (const loan of loans) {
      if (loan.basketName) {
        const bid = String(loan.basket._id);
        if (seenBaskets.has(bid)) continue; // one card per named basket
        seenBaskets.add(bid);
        cards.push({
          ...loan, // basket-level LTV fields are on the loan object
          cardTitle: loan.basketName,
          memberLoans: loans.filter((l) => l.basket && String(l.basket._id) === bid),
          isBasketCard: true,
        });
      } else if (loan.currentLTV != null || loan.ltvStatus === "NO_PRICE") {
        cards.push({
          ...loan,
          cardTitle: `${loan.lender} — ${loan.borrowerCompany}`,
          memberLoans: [loan],
          isBasketCard: false,
        });
      }
    }
    return cards;
  }, [loans]);

  const cardsWithCollateral = useMemo(
    () =>
      monitorCards
        .filter((c) => c.currentLTV != null)
        .sort((a, b) => {
          const order = { BREACH: 0, WARNING: 1, SAFE: 2, RELEASE_ELIGIBLE: 3, NO_COLLATERAL: 4 };
          return (order[a.ltvStatus] ?? 5) - (order[b.ltvStatus] ?? 5);
        }),
    [monitorCards]
  );

  const cardsWithoutCollateral = useMemo(
    () =>
      monitorCards.filter((c) => c.currentLTV == null),
    [monitorCards]
  );

  if (loans.length === 0) {
    return <div className="cp-empty">אין הלוואות פעילות</div>;
  }

  return (
    <div className="cp-ltv-monitor">
      {cardsWithCollateral.map((card) =>
        card.isBasketCard ? (
          <BasketLTVCard key={`basket-${card.basket._id}`} card={card} />
        ) : (
          <LTVCard key={card._id} loan={card} />
        )
      )}
      {cardsWithoutCollateral.length > 0 && (
        <>
          <div className="cp-section-divider">הלוואות ללא בטוחת מניות</div>
          {cardsWithoutCollateral.map((card) =>
            card.isBasketCard ? (
              <BasketLTVCard key={`basket-${card.basket._id}`} card={card} />
            ) : (
              <LTVCard key={card._id} loan={card} />
            )
          )}
        </>
      )}
    </div>
  );
}

// ─── Tab 2: Pledge Register ────────────────────────────────────────────────────

function PledgeRegister({ pledges }) {
  // Group by lien holder (the bank/lender holding the lien on each pledge).
  const groups = useMemo(() => {
    const map = {};
    pledges.forEach((p) => {
      const key = p.lienHolder || "—";
      if (!map[key]) map[key] = [];
      map[key].push(p);
    });
    return Object.entries(map).sort((a, b) => a[0].localeCompare(b[0]));
  }, [pledges]);

  if (pledges.length === 0) {
    return <div className="cp-empty">אין שעבודים רשומים</div>;
  }

  return (
    <div className="cp-pledge-register">
      {groups.map(([lienHolder, rows]) => {
        const totalUnits = rows.reduce((s, p) => s + (p.pledgedUnits || 0), 0);
        return (
          <div key={lienHolder} className="cp-pledge-group">
            <div className="cp-pledge-group-header">
              <span className="cp-pledge-group-name">{lienHolder}</span>
              <span className="cp-pledge-group-meta">
                {rows.length} שעבודים{totalUnits > 0 ? ` · ${formatNum(totalUnits)} מניות` : ""}
              </span>
            </div>
            <div className="cp-pledge-table-wrap">
              <table className="cp-pledge-table">
                <thead>
                  <tr>
                    <th>נכס</th>
                    <th>סמל</th>
                    <th>יחידות משועבדות</th>
                    <th>גוף משעבד</th>
                    <th>מסגרת</th>
                    <th>עדיפות שיעבוד</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((p) => {
                    const asset = p.assetId || {};
                    const basket = p.basketId;
                    return (
                      <tr key={p._id}>
                        <td>{asset.assetName || "—"}</td>
                        <td>{asset.symbol ? <span className="cp-symbol-badge">{asset.symbol}</span> : "—"}</td>
                        <td>{p.pledgedUnits != null ? formatNum(p.pledgedUnits) : "נכס בשלמותו"}</td>
                        <td>{p.pledgingCompany || "—"}</td>
                        <td>{basket && !basket.isSingleton ? basket.name : "—"}</td>
                        <td>{LIEN_POSITION_LABELS[p.lienPosition] || p.lienPosition || "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Tab 3: Asset Capacity ─────────────────────────────────────────────────────

const ASSET_TYPE_LABELS = {
  STOCK: "מניה",
  REAL_ESTATE: 'נדל"ן',
  LAND: "קרקע",
  LOAN_RECEIVABLE: "הלוואה שניתנה",
  PRIVATE_EQUITY: "הון פרטי",
  CASH: "מזומן",
  OTHER: "אחר",
};

const LIEN_POSITION_LABELS = { FIRST: "ראשון", SECOND: "שני", THIRD: "שלישי", OTHER: "אחר" };

// ─── Asset Form Modal ──────────────────────────────────────────────────────────

function AssetFormModal({ asset, onClose, onSaved }) {
  const blank = {
    assetType: "STOCK", assetName: "", currency: "ILS", notes: "", owningCompany: "",
    // STOCK fields
    symbol: "", totalUnits: "", minimumFreeRequired: "", minimumFreeReason: "",
    // Generic fields (non-STOCK)
    value: "", annualCashFlow: "",
  };
  const [form, setForm] = useState(asset ? {
    ...blank,
    assetType:           asset.assetType || "STOCK",
    assetName:           asset.assetName || "",
    currency:            asset.currency || "ILS",
    notes:               asset.notes || "",
    owningCompany:       asset.owningCompany || "",
    symbol:              asset.symbol || "",
    totalUnits:          asset.totalUnits != null ? String(asset.totalUnits) : "",
    minimumFreeRequired: asset.minimumFreeRequired != null ? String(asset.minimumFreeRequired) : "",
    minimumFreeReason:   asset.minimumFreeReason || "",
    value:               asset.value != null ? String(asset.value) : "",
    annualCashFlow:      asset.annualCashFlow != null ? String(asset.annualCashFlow) : "",
  } : blank);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const set = (name, value) => setForm(prev => ({ ...prev, [name]: value }));
  const onChange = (e) => set(e.target.name, e.target.value);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload = { ...form };
      ["totalUnits", "minimumFreeRequired", "value", "annualCashFlow"].forEach(f => {
        if (payload[f] !== "" && payload[f] != null) payload[f] = Number(payload[f]);
        else delete payload[f];
      });
      ["symbol", "minimumFreeReason", "notes"].forEach(f => { if (!payload[f]) delete payload[f]; });

      if (asset?._id) {
        await apiClient.put(`/asset-holdings/${asset._id}`, payload);
      } else {
        await apiClient.post(`/asset-holdings`, payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setSaving(false);
    }
  };

  const t = form.assetType;
  return (
    <div className="cp-modal-overlay" onClick={onClose}>
      <div className="cp-modal-box" onClick={e => e.stopPropagation()}>
        <div className="cp-modal-header">
          <h3 className="cp-modal-title">{asset?._id ? "עריכת נכס" : "הוסף נכס"}</h3>
          <button type="button" onClick={onClose} className="cp-modal-close">×</button>
        </div>
        {error && <div className="cp-error cp-modal-error">{error}</div>}
        <form onSubmit={handleSubmit} className="form-grid cp-modal-form">

          <div className="field form-full-width">
            <label className="label-required">סוג נכס</label>
            <select className="select" name="assetType" value={form.assetType} onChange={onChange}>
              {Object.entries(ASSET_TYPE_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>

          <div className="field form-full-width">
            <label className="label-required">שם נכס</label>
            <input className="input" name="assetName" value={form.assetName} onChange={onChange} placeholder="למשל: קבוצת דלק, דירה בתל אביב" required />
          </div>

          <CompanySelect allowAdd value={form.owningCompany} onChange={onChange} />

          {t === "STOCK" && (
            <>
              <div className="field">
                <label className="label-required">סמל מניה</label>
                <input className="input" name="symbol" value={form.symbol} onChange={onChange} placeholder="DLEKG.TA" />
              </div>
              <div className="field">
                <label className="label-required">סה״כ מניות בבעלות</label>
                <NumericInput className="input" name="totalUnits" value={form.totalUnits} onChange={onChange} placeholder="0" required />
              </div>
              <div className="field">
                <label className="label-optional">מניות מוגבלות / שמורות</label>
                <NumericInput className="input" name="minimumFreeRequired" value={form.minimumFreeRequired} onChange={onChange} placeholder="0" />
              </div>
              <div className="field form-full-width">
                <label className="label-optional">סיבת ההגבלה</label>
                <input className="input" name="minimumFreeReason" value={form.minimumFreeReason} onChange={onChange} placeholder="20% מהון רשום למסחר" />
              </div>
            </>
          )}

          {t !== "STOCK" && (
            <>
              <div className="field">
                <label className="label-required">שווי (₪)</label>
                <NumericInput className="input" name="value" value={form.value} onChange={onChange} placeholder="0" required />
              </div>
              <div className="field">
                <label className="label-optional">תזרים שנתי (₪)</label>
                <NumericInput className="input" name="annualCashFlow" value={form.annualCashFlow} onChange={onChange} placeholder="0" />
              </div>
            </>
          )}

          <div className="field form-full-width">
            <label className="label-optional">הערות</label>
            <input className="input" name="notes" value={form.notes} onChange={onChange} placeholder="הערות נוספות" />
          </div>

          <div className="form-full-width cp-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>ביטול</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? "שומר..." : asset?._id ? "עדכון" : "הוסף"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Capacity Bar ──────────────────────────────────────────────────────────────

function CapacityBar({ pledgedPct, freePct, minimumFreePct }) {
  const usablePct = Math.max(0, freePct - (minimumFreePct || 0));
  const reservedPct = Math.min(freePct, minimumFreePct || 0);
  return (
    <div className="cp-capacity-bar-wrap">
      <div className="cp-capacity-bar">
        <div className="cp-cap-pledged" style={{ width: `${Math.min(pledgedPct, 100)}%` }} />
        <div className="cp-cap-reserved" style={{ width: `${Math.min(reservedPct, 100)}%` }} />
        <div className="cp-cap-usable" style={{ width: `${Math.min(usablePct, 100)}%` }} />
      </div>
      <div className="cp-capacity-legend">
        <span className="cp-legend-item pledged"><span className="cp-legend-dot" />משועבד {formatPct(pledgedPct, 0)}</span>
        {reservedPct > 0 && <span className="cp-legend-item reserved"><span className="cp-legend-dot" />שמור {formatPct(reservedPct, 0)}</span>}
        <span className="cp-legend-item usable"><span className="cp-legend-dot" />פנוי לשימוש {formatPct(usablePct, 0)}</span>
      </div>
    </div>
  );
}

// The basket a pledge sits in, named the way a reader would name it: a real
// framework by its name, a singleton by the standalone loan it wraps — the
// singleton itself is an implementation detail and is never shown as one.
function pledgeHolder(pledge, loansByBasket) {
  if (pledge.basketName) return { text: pledge.basketName, to: null };
  const members = loansByBasket.get(String(pledge.basketId)) || [];
  if (members.length === 1) {
    const loan = members[0];
    return {
      text: loan.borrowerCompany ? `${loan.lender} — ${loan.borrowerCompany}` : loan.lender,
      to: `/loan/${loan._id}`,
    };
  }
  if (members.length > 1) return { text: `${members.length} הלוואות`, to: null };
  return { text: "—", to: null };
}

// Where the asset actually went: one row per pledge, then what is left over.
// The rows sum to the whole position, so a reader can check the arithmetic.
function AssetDivision({ group, loansByBasket }) {
  const isStock = group.assetType === "STOCK";
  const cols = 3 + (isStock ? 2 : 0) + (group.isMerged ? 1 : 0);

  return (
    <div className="cp-division">
      <div className="cp-division-title">חלוקת הנכס</div>
      <div className="cp-division-table-wrap">
        <table className="cp-division-table">
          <thead>
            <tr>
              <th>משועבד ל</th>
              <th>מסגרת / הלוואה</th>
              {isStock && <th>יחידות</th>}
              {isStock && <th>% מהנכס</th>}
              <th>עדיפות שעבוד</th>
              {group.isMerged && <th>רשומה</th>}
            </tr>
          </thead>
          <tbody>
            {group.pledges.length === 0 && (
              <tr>
                <td colSpan={cols} className="cp-division-empty">אין שעבודים על נכס זה</td>
              </tr>
            )}
            {group.pledges.map((p) => {
              const holder = pledgeHolder(p, loansByBasket);
              return (
                <tr key={p._id}>
                  <td className="cp-division-lender">{p.lienHolder || "—"}</td>
                  <td>
                    {holder.to
                      ? <Link to={holder.to} className="cp-table-link">{holder.text}</Link>
                      : holder.text}
                  </td>
                  {isStock && <td>{formatNum(p.pledgedUnits)}</td>}
                  {isStock && <td>{formatPct(p.pctOfPosition)}</td>}
                  <td>{LIEN_POSITION_LABELS[p.lienPosition] || p.lienPosition || "—"}</td>
                  {group.isMerged && <td className="cp-division-source">{p.sourceAssetName}</td>}
                </tr>
              );
            })}

            {isStock && group.restrictedUnits > 0 && (
              <tr className="cp-division-row-reserved">
                <td className="cp-division-lender">מוגבל / שמור</td>
                <td>{group.minimumFreeReason || "—"}</td>
                <td>{formatNum(group.restrictedUnits)}</td>
                <td>{formatPct(group.restrictedPct)}</td>
                <td>—</td>
                {group.isMerged && <td>—</td>}
              </tr>
            )}

            {isStock && (
              <tr className="cp-division-row-free">
                <td className="cp-division-lender">זמין לשעבוד</td>
                <td>—</td>
                <td>{formatNum(group.usableUnits)}</td>
                <td>{formatPct(group.usablePct)}</td>
                <td>—</td>
                {group.isMerged && <td>—</td>}
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// The registry rows behind a merged group. Editing and deleting act on a single
// record, so once several rows stand behind one card the actions have to move
// here — the card header can no longer name which record it would change.
function AssetSources({ group, canColEdit, canColRemove, onEdit, onDelete, deletingId, confirmDeleteId, setConfirmDeleteId }) {
  return (
    <div className="cp-division">
      <div className="cp-division-title">רשומות במאגר · {group.sources.length}</div>
      {group.sources.map((s) => (
        <div key={s._id} className="cp-source-row">
          <span className="cp-source-name">{s.assetName}</span>
          <span className="cp-source-meta">
            {group.assetType === "STOCK" ? `${formatNum(s.totalUnits)} יחידות` : formatILS(s.value, true)}
            {s.owningCompany ? ` · ${s.owningCompany}` : ""}
          </span>
          {(canColEdit || canColRemove) && (
            <span className="cp-source-actions">
              {canColEdit && <button type="button" className="ag-grid-action-btn" onClick={() => onEdit(s)}>עריכה</button>}
              {canColRemove && (confirmDeleteId === s._id ? (
                <>
                  <span className="cp-source-confirm">בטוח?</span>
                  <button type="button" className="ag-grid-action-btn ag-grid-action-btn-danger" onClick={() => onDelete(s._id)} disabled={deletingId === s._id}>כן</button>
                  <button type="button" className="ag-grid-action-btn" onClick={() => setConfirmDeleteId(null)}>ביטול</button>
                </>
              ) : (
                <button type="button" className="ag-grid-action-btn ag-grid-action-btn-danger" onClick={() => setConfirmDeleteId(s._id)}>מחיקה</button>
              ))}
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export function AssetCapacity({ groups, loans, loadingHoldings, onRefresh, canEdit, canColEdit, canColRemove }) {
  const [modalAsset, setModalAsset] = useState(null); // null=closed, false=new, obj=edit
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);

  // Singleton baskets carry no name, so the loan is the only label a pledge on
  // one can be given. Built once per render rather than per pledge row.
  const loansByBasket = useMemo(() => {
    const map = new Map();
    (loans || []).forEach((l) => {
      if (!l.basket?._id) return;
      const key = String(l.basket._id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    });
    return map;
  }, [loans]);

  const [filter, setFilter] = useState("");
  const filteredGroups = useMemo(() => {
    if (!filter.trim()) return groups;
    const q = filter.trim().toLowerCase();
    const hit = (v) => String(v || "").toLowerCase().includes(q);
    return groups.filter((g) =>
      hit(g.assetName) || hit(g.symbol) || hit(ASSET_TYPE_LABELS[g.assetType]) ||
      g.sources.some((s) => hit(s.assetName)) ||
      g.owningCompanies.some(hit) ||
      g.pledges.some((p) => hit(p.lienHolder) || hit(p.basketName))
    );
  }, [groups, filter]);

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await apiClient.delete(`/asset-holdings/${id}`);
      setConfirmDeleteId(null);
      onRefresh();
    } catch (err) {
      setDeletingId(null);
      alert(err.response?.data?.error || err.message);
    } finally {
      setDeletingId(null);
    }
  };

  if (loadingHoldings) return <div className="cp-loading">טוען נתוני נכסים...</div>;

  return (
    <div className="cp-asset-capacity">
      <div className="cp-capacity-toolbar">
        <input
          className="input cp-capacity-search"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="חיפוש לפי שם, סמל, סוג..."
        />
        {canEdit && <button type="button" className="btn btn-primary btn-sm" onClick={() => setModalAsset(false)}>+ הוסף נכס</button>}
      </div>

      {groups.length === 0 && (
        <div className="cp-empty-state">
          <div className="cp-empty-icon">📦</div>
          <div className="cp-empty-title">אין נכסים מוגדרים</div>
          <div className="cp-empty-body">הוסף מניות, נדל״ן, קרקע, הון פרטי או הלוואות שנתת כדי לעקוב אחר קיבולת הבטוחות.</div>
          {canEdit && <button type="button" className="btn btn-primary" style={{ marginTop: 16 }} onClick={() => setModalAsset(false)}>+ הוסף נכס ראשון</button>}
        </div>
      )}

      {filter.trim() && filteredGroups.length === 0 && (
        <div className="cp-empty">לא נמצאו נכסים התואמים לחיפוש</div>
      )}

      {filteredGroups.map((g) => {
        const expanded = expandedKey === g.key;
        const sole = g.isMerged ? null : g.sources[0];
        return (
          <div key={g.key} className="cp-holding-card">
            <div className="cp-holding-header">
              <div className="cp-holding-identity">
                {g.logoUrl
                  ? <img src={g.logoUrl} alt={g.symbol} style={{ width: 32, height: 32, borderRadius: 6, objectFit: "contain", background: "#fff", border: "1px solid var(--border-light)" }} />
                  : <span className="cp-symbol-badge large">{g.symbol || ASSET_TYPE_LABELS[g.assetType]?.slice(0, 2)}</span>
                }
                <div>
                  <div className="cp-holding-name">
                    {g.assetType === "STOCK" && g.symbol
                      ? <Link to={`/asset/stock/${g.symbol}`} style={{ color: "inherit", textDecoration: "none", borderBottom: "1px dashed var(--border)" }}>{g.assetName}</Link>
                      : g.assetName
                    }
                    {g.isMerged && <span className="cp-merge-badge">{g.sources.length} רשומות</span>}
                  </div>
                  <div className="cp-holding-type">
                    {ASSET_TYPE_LABELS[g.assetType] || g.assetType}{g.symbol ? ` · ${g.symbol}` : ""} · {g.owningCompanies.join(" · ") || "—"}
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                {g.currentPrice != null && (
                  <div className="cp-holding-price">
                    <span className="cp-price-value">{formatILS(g.currentPrice)}</span>
                    {g.priceChangePct != null && (
                      <span style={{ fontSize: 12, fontWeight: 600, color: g.priceChangePct >= 0 ? "var(--success)" : "var(--error)" }}>
                        {g.priceChangePct >= 0 ? "▲" : "▼"} {Math.abs(g.priceChangePct).toFixed(2)}%
                      </span>
                    )}
                    <span className="cp-price-label">מחיר נוכחי (₪)</span>
                    {g.stockPriceDate && (
                      <span style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 2 }}>נכון ל-{formatDate(g.stockPriceDate)}</span>
                    )}
                  </div>
                )}
                {g.marketCap != null && (
                  <div className="cp-holding-price">
                    <span className="cp-price-value" style={{ fontSize: 14 }}>{formatILS(g.marketCap, true)}</span>
                    <span className="cp-price-label">שווי שוק</span>
                  </div>
                )}
                {sole && (canColEdit || canColRemove) && (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {canColEdit && <button type="button" className="ag-grid-action-btn" onClick={() => setModalAsset(sole)}>עריכה</button>}
                    {canColRemove && (confirmDeleteId === sole._id ? (
                      <>
                        <span style={{ fontSize: 12, color: "var(--text-secondary)" }}>בטוח?</span>
                        <button type="button" className="ag-grid-action-btn ag-grid-action-btn-danger" onClick={() => handleDelete(sole._id)} disabled={deletingId === sole._id}>כן</button>
                        <button type="button" className="ag-grid-action-btn" onClick={() => setConfirmDeleteId(null)}>ביטול</button>
                      </>
                    ) : (
                      <button type="button" className="ag-grid-action-btn ag-grid-action-btn-danger" onClick={() => setConfirmDeleteId(sole._id)}>מחיקה</button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  className="cp-expand-btn"
                  aria-expanded={expanded}
                  aria-label={expanded ? "כווץ" : "הרחב"}
                  onClick={() => setExpandedKey(expanded ? null : g.key)}
                >
                  <span className={`cp-expand-chevron${expanded ? " open" : ""}`} />
                </button>
              </div>
            </div>

            {g.assetType === "STOCK" && (
              <>
                <div className="cp-holding-stats">
                  <div className="cp-holding-stat">
                    <span className="cp-stat-label">סה״כ מניות</span>
                    <span className="cp-stat-value">{formatNum(g.totalUnits)}</span>
                  </div>
                  <div className="cp-holding-stat">
                    <span className="cp-stat-label">משועבד</span>
                    <span className="cp-stat-value pledged">{formatNum(g.pledgedUnits)}</span>
                  </div>
                  <div className="cp-holding-stat">
                    <span className="cp-stat-label">לא משועבד</span>
                    <span className="cp-stat-value">{formatNum(g.freeUnits)}</span>
                  </div>
                  <div className="cp-holding-stat">
                    <span className="cp-stat-label">מוגבל / שמור</span>
                    <span className="cp-stat-value reserved">{formatNum(g.restrictedUnits)}</span>
                  </div>
                  <div className="cp-holding-stat highlighted">
                    <span className="cp-stat-label">זמין לשעבוד</span>
                    <span className="cp-stat-value usable">{formatNum(g.usableUnits)}</span>
                  </div>
                </div>
                <CapacityBar
                  pledgedPct={g.pledgedPct || 0}
                  freePct={g.freePct || 0}
                  minimumFreePct={g.restrictedPct || 0}
                />
                {g.totalValue != null && (
                  <div className="cp-holding-values">
                    <div className="cp-holding-value-item"><span className="cp-value-label">שווי כולל</span><span className="cp-value-amount">{formatILS(g.totalValue, true)}</span></div>
                    <div className="cp-holding-value-item"><span className="cp-value-label">שווי משועבד</span><span className="cp-value-amount">{formatILS(g.pledgedValue, true)}</span></div>
                    <div className="cp-holding-value-item"><span className="cp-value-label">שווי מוגבל</span><span className="cp-value-amount">{formatILS(g.restrictedValue, true)}</span></div>
                    <div className="cp-holding-value-item highlighted"><span className="cp-value-label">שווי זמין לשימוש</span><span className="cp-value-amount">{formatILS(g.usableValue, true)}</span></div>
                  </div>
                )}
                <div className="cp-holding-summary-line">
                  {g.pledges.length > 0
                    ? `משועבד ב-${g.pledges.length} שעבודים · ${formatPct(g.pledgedPct)} מהפוזיציה`
                    : "אין שעבודים על נכס זה"}
                </div>
                {g.minimumFreeReason && <div className="cp-holding-reason">{g.minimumFreeReason}</div>}
              </>
            )}

            {g.assetType !== "STOCK" && (
              <>
                <div className="cp-holding-stats">
                  <div className="cp-holding-stat"><span className="cp-stat-label">שווי (₪)</span><span className="cp-stat-value">{formatILS(g.value, true)}</span></div>
                  {g.annualCashFlow > 0 && <div className="cp-holding-stat"><span className="cp-stat-label">תזרים שנתי</span><span className="cp-stat-value">{formatILS(g.annualCashFlow, true)}</span></div>}
                </div>
                <div className="cp-holding-summary-line">
                  {g.pledges.length > 0 ? `משועבד ב-${g.pledges.length} שעבודים` : "חופשי לשעבוד"}
                </div>
              </>
            )}

            {expanded && (
              <>
                <AssetDivision group={g} loansByBasket={loansByBasket} />
                {g.isMerged && (
                  <AssetSources
                    group={g}
                    canColEdit={canColEdit}
                    canColRemove={canColRemove}
                    onEdit={setModalAsset}
                    onDelete={handleDelete}
                    deletingId={deletingId}
                    confirmDeleteId={confirmDeleteId}
                    setConfirmDeleteId={setConfirmDeleteId}
                  />
                )}
              </>
            )}

            {g.notes && <div className="cp-holding-reason">{g.notes}</div>}
          </div>
        );
      })}

      {modalAsset !== null && (
        <AssetFormModal
          asset={modalAsset || null}
          onClose={() => setModalAsset(null)}
          onSaved={onRefresh}
        />
      )}
    </div>
  );
}

// ─── Tab 4: Guarantees ─────────────────────────────────────────────────────────

const GUARANTEE_STATUS_LABELS = { ACTIVE: "פעיל", RELEASED: "שוחרר", EXPIRED: "פג תוקף" };

function Guarantees({ guarantees }) {
  const totalGuarantees = useMemo(
    () => guarantees.reduce((s, g) => s + (g.amount || 0), 0),
    [guarantees]
  );

  if (guarantees.length === 0) {
    return <div className="cp-empty">אין ערבויות רשומות</div>;
  }

  return (
    <div className="cp-guarantees">
      <div className="cp-guarantees-total">
        <span className="cp-total-label">סה״כ ערבויות</span>
        <span className="cp-total-value">{formatILS(totalGuarantees, true)}</span>
      </div>

      <div className="cp-guarantee-table-wrap">
        <table className="cp-guarantee-table">
          <thead>
            <tr>
              <th>ערב</th>
              <th>סוג</th>
              <th>סכום</th>
              <th>מסגרת / הלוואה</th>
              <th>תוקף</th>
              <th>סטטוס</th>
            </tr>
          </thead>
          <tbody>
            {guarantees.map((g) => {
              // basketId is populated: { _id, name, isSingleton }
              const basket = g.basketId;
              const groupLabel = basket ? (basket.isSingleton ? "הלוואה עצמאית" : basket.name) : "—";
              const expiringSoon = g.status === "ACTIVE" && isExpiringSoon(g.expiryDate);
              return (
                <tr key={g._id}>
                  <td>{g.guarantorName || "—"}</td>
                  <td>{g.guaranteeType === "PERSONAL" ? "אישית" : "תאגידית"}</td>
                  <td>{formatILS(g.amount, true)}</td>
                  <td>{groupLabel}</td>
                  <td><span className={expiringSoon ? "text-warning" : undefined}>{formatDate(g.expiryDate)}</span></td>
                  <td>
                    <span className={`cp-status-badge ${g.status === "ACTIVE" ? "status-safe" : "status-none"}`}>
                      {GUARANTEE_STATUS_LABELS[g.status] || g.status || "—"}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

const TABS = [
  { id: "ltv", label: "מוניטור LTV" },
  { id: "capacity", label: "קיבולת נכסים" },
  { id: "pledge", label: "רישום שעבוד" },
  { id: "guarantees", label: "ערבויות" },
  { id: "baskets", label: "מסגרות" },
];

export default function CollateralPage({ onUnauthorized, canEdit }) {
  const user = useCurrentUser();
  const canViewPulse  = can(user, 'collateral.viewPulse');
  const canViewGauges = can(user, 'collateral.viewGauges');
  const canViewCards  = can(user, 'collateral.viewCards');
  // Asset Capacity manages AssetHolding records. Honor either the dedicated
  // collateral permission or the balance-sheet asset-management permission.
  const canManageAssets = can(user, 'balanceSheet.manageAssets');
  // `canEdit` is the route-level editing capability already granted by App.
  // Keep this as a fallback for older user profiles that predate granular
  // collateral permissions.
  const canColCreate  = can(user, 'collateral.create') || canManageAssets || canEdit;
  const canColEdit    = can(user, 'collateral.edit') || canManageAssets || canEdit;
  const canColRemove  = can(user, 'collateral.remove') || canManageAssets || canEdit;
  const [loans, setLoans] = useState([]);
  const [pledges, setPledges] = useState([]);
  const [guarantees, setGuarantees] = useState([]);
  const [holdings, setHoldings] = useState([]);
  const [activeTab, setActiveTab] = useState("ltv");
  const [loading, setLoading] = useState(true);
  const [loadingHoldings, setLoadingHoldings] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [loansRes, pledgesRes, guaranteesRes] = await Promise.all([
        apiClient.get(`/loans`),
        apiClient.get(`/pledges?status=ACTIVE`),
        apiClient.get(`/guarantees`),
      ]);
      setLoans(loansRes.data || []);
      setPledges(pledgesRes.data || []);
      setGuarantees(guaranteesRes.data || []);
    } catch (err) {
      if (err.response?.status === 401 && onUnauthorized) onUnauthorized();
      else setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [onUnauthorized]);

  const fetchHoldings = useCallback(async () => {
    setLoadingHoldings(true);
    try {
      const res = await apiClient.get(`/asset-holdings`);
      setHoldings(res.data || []);
    } catch {
      setHoldings([]);
    } finally {
      setLoadingHoldings(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchHoldings();
  }, [fetchData, fetchHoldings]);

  const activeLoans = useMemo(() => loans.filter((l) => l.status !== "repaid"), [loans]);

  // One entry per asset, not per registry row — see utils/assetGrouping.
  const assetGroups = useMemo(() => groupHoldingsByAsset(holdings), [holdings]);

  // Build monitor cards (deduped by basket) to avoid double-counting in badges
  const monitorCards = useMemo(() => {
    const cards = [];
    const seenBaskets = new Set();
    for (const loan of activeLoans) {
      if (loan.basketName) {
        const bid = String(loan.basket._id);
        if (seenBaskets.has(bid)) continue;
        seenBaskets.add(bid);
        cards.push({
          ...loan,
          cardTitle: loan.basketName,
          memberLoans: activeLoans.filter((l) => l.basket && String(l.basket._id) === bid),
          isBasketCard: true,
        });
      } else {
        cards.push({ ...loan, isBasketCard: false });
      }
    }
    return cards;
  }, [activeLoans]);

  // Count alerts for tab badges — count monitorCards to avoid double-counting basket members
  const tabBadges = useMemo(() => {
    const breach = monitorCards.filter((c) => c.ltvStatus === "BREACH").length;
    const warning = monitorCards.filter((c) => c.ltvStatus === "WARNING").length;
    const release = monitorCards.filter((c) => c.ltvStatus === "RELEASE_ELIGIBLE").length;

    // Capacity: stock assets with zero usable capacity (fully pledged/reserved).
    // Counted per asset, so a symbol split across rows raises one badge, not three.
    const capacity = assetGroups.filter(
      (g) => g.assetType === "STOCK" && g.totalUnits > 0 && g.pledgedUnits > 0 && (g.usableUnits ?? 0) === 0
    ).length;

    // Guarantees: active guarantees expiring within 90 days
    const guaranteesExpiring = guarantees.filter((g) => g.status === "ACTIVE" && isExpiringSoon(g.expiryDate)).length;

    return { ltv: breach + warning, release, capacity, guarantees: guaranteesExpiring };
  }, [monitorCards, guarantees, assetGroups]);

  return (
    <div className="cp-page">
      {/* Pulse Header */}
      {canViewPulse && !loading && <PulseHeader loans={activeLoans} holdings={holdings} />}

      {/* Tab Bar */}
      <div className="cp-tab-bar">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`cp-tab-btn ${activeTab === tab.id ? "active" : ""}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
            {tab.id === "ltv" && tabBadges.ltv > 0 && (
              <span className="cp-tab-badge">{tabBadges.ltv}</span>
            )}
            {tab.id === "ltv" && tabBadges.release > 0 && (
              <span className="cp-tab-badge cp-tab-badge-release">{tabBadges.release}</span>
            )}
            {tab.id === "capacity" && tabBadges.capacity > 0 && (
              <span className="cp-tab-badge">{tabBadges.capacity}</span>
            )}
            {tab.id === "guarantees" && tabBadges.guarantees > 0 && (
              <span className="cp-tab-badge cp-tab-badge-warning">{tabBadges.guarantees}</span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="cp-content">
        {error && <div className="cp-error">{error}</div>}

        {loading ? (
          <div className="cp-loading">טוען נתונים...</div>
        ) : (
          <>
            {canViewGauges && activeTab === "ltv" && <LTVMonitor loans={activeLoans} />}
            {activeTab === "pledge" && <PledgeRegister pledges={pledges} />}
            {canViewCards && activeTab === "capacity" && (
              <AssetCapacity groups={assetGroups} loans={activeLoans} loadingHoldings={loadingHoldings} onRefresh={fetchHoldings} canEdit={canColCreate} canColEdit={canColEdit} canColRemove={canColRemove} />
            )}
            {activeTab === "guarantees" && <Guarantees guarantees={guarantees} />}
            {activeTab === "baskets" && <BasketsTab />}
          </>
        )}
      </div>
    </div>
  );
}
